import type { FC, HTMLAttributes } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useModifiers } from '../../styles/hooks/useModifiers';

import { generateNumberSlideKeyframes, generateSequence } from './utils';

import styles from './NumberSlider.module.css';

export interface NumberSliderProps extends HTMLAttributes<HTMLDivElement> {
  from: number;
  to: number;
  duration: number;
}

export const NumberSlider: FC<NumberSliderProps> = ({ className, from, to, duration }) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const [isAnimate, setIsAnimate] = useState<boolean>(false);
  const [maxChars, setMaxChars] = useState<number>(0);

  const items = useMemo(() => {
    const values = generateSequence(from, to, duration);
    setMaxChars(Math.max(String(from).length, String(to).length));
    return values.map((value, index) => (
      <li key={`${value}-${index}`} className={styles.sliderContainer__listItem}>
        {value}
      </li>
    ));
  }, [duration, from, to]);

  const mods = useModifiers(styles, 'sliderContainer', {}, { withBaseClass: true, extraClassName: className });

  useEffect(() => {
    setIsAnimate(true);
  }, [items]);

  useEffect(() => {
    if (!listRef.current) return;

    if (isAnimate) {
      const keyframes = generateNumberSlideKeyframes(items.length, from > to);
      const animation = listRef.current.animate(keyframes, { duration, easing: 'ease-out' });

      animation.onfinish = (): void => {
        setIsAnimate(false);
        setMaxChars(String(to).length);
      };
      return (): void => animation.cancel();
    }
  }, [from, to, duration, items, isAnimate]);

  return (
    <div className={mods}>
      <ul
        ref={listRef}
        className={styles.sliderContainer__list}
        style={{ '--max-chars': maxChars + 'ch' } as React.CSSProperties}
      >
        {isAnimate ? (
          items
        ) : (
          <li data-testid="number-slider-list-item" className={styles.sliderContainer__listItem}>
            {to}
          </li>
        )}
      </ul>
    </div>
  );
};
